import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { taskService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, Loader2 } from "lucide-react";

const TYPES = ["Task", "FollowUp"];
const PRIORITIES = ["Low", "Medium", "High"];
const STATUSES = ["Open", "InProgress", "Done"];
const PRIO_COLOR = { High: "red", Medium: "amber", Low: "slate" };
const EMPTY = { title: "", description: "", type: "Task", priority: "Medium", status: "Open", dueDate: "", relatedType: "", relatedId: "", tenantId: "" };

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-foreground mb-1">{label}</label>{children}</div>;
}

export default function TasksPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await taskService.list({ status: statusFilter || undefined, type: typeFilter || undefined, size: 100 }); setRows(d.items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [statusFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const openCreate = () => { setForm(EMPTY); setOpen(true); };

  const save = async () => {
    if (!form.title) return toast.error("Title is required");
    if (isSuper && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      await taskService.create(payload);
      toast.success("Task created"); setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const complete = async (r) => {
    try { await taskService.complete(r.id); toast.success("Task completed"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete task "${r.title}"?`)) return;
    try { await taskService.remove(r.id); toast.success("Task deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canCreate = hasPermission("TASK.CREATE");
  const canEdit = hasPermission("TASK.EDIT");
  const canDelete = hasPermission("TASK.DELETE");

  const columns = [
    { key: "title", header: "Title", render: (r) => <span className={`font-medium ${r.status === "Done" ? "line-through text-muted-foreground" : "text-foreground"}`}>{r.title}</span> },
    { key: "type", header: "Type", render: (r) => <Badge color={r.type === "FollowUp" ? "teal" : "slate"}>{r.type === "FollowUp" ? "Follow-up" : "Task"}</Badge> },
    { key: "priority", header: "Priority", render: (r) => <Badge color={PRIO_COLOR[r.priority] || "slate"}>{r.priority}</Badge> },
    { key: "status", header: "Status", render: (r) => <Badge color={r.status === "Done" ? "green" : r.status === "InProgress" ? "amber" : "blue"}>{r.status}</Badge> },
    { key: "dueDate", header: "Due", render: (r) => r.dueDate || "—" },
    {
      key: "_a", header: "",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          {canEdit && r.status !== "Done" && <button onClick={() => complete(r)} className="p-1.5 rounded-md hover:bg-emerald-500/10 text-emerald-600" data-testid={`task-complete-${r.id}`}><CheckCircle2 className="h-4 w-4" /></button>}
          {canDelete && <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`task-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="tasks-page">
      <PageHeader title="Tasks & Follow-ups" subtitle="Track sales tasks and customer follow-ups"
        actions={canCreate && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="task-create-btn">
            <Plus className="h-4 w-4" /> New Task
          </button>
        )}
      />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="task-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="task-type-filter">
          <option value="">All types</option>
          {TYPES.map((t) => <option key={t} value={t}>{t === "FollowUp" ? "Follow-up" : "Task"}</option>)}
        </select>
      </div>
      <DataTable columns={columns} rows={rows} loading={loading} testid="tasks-table" empty="No tasks yet" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="task-dialog">
            <h3 className="text-lg font-bold mb-4">New Task</h3>
            <div className="space-y-3.5">
              {isSuper && (
                <Field label="Tenant *">
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} className="ciq-input" data-testid="task-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Title *"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="ciq-input" data-testid="task-title" /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Type"><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="ciq-input" data-testid="task-type">{TYPES.map((t) => <option key={t} value={t}>{t === "FollowUp" ? "Follow-up" : "Task"}</option>)}</select></Field>
                <Field label="Priority"><select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="ciq-input">{PRIORITIES.map((p) => <option key={p}>{p}</option>)}</select></Field>
                <Field label="Due Date"><input type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} className="ciq-input" data-testid="task-due" /></Field>
              </div>
              <Field label="Description"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="ciq-input" rows={2} /></Field>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="task-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
